import React from "react";
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {
    SuscripcionCampoAdicionalVista,
    TIPO_CAMPO_ADICIONAL,
} from "../../types/camposAdicionales";
import { SectionTitle } from "./SectionTitle";
import { checkboxStyle, checkboxLabelStyle, textFieldStyle } from "./CouponStyles";

interface CamposAdicionalesSectionProps {
    lstCampos: SuscripcionCampoAdicionalVista[];
    valores: Record<string, string>;
    onChange: (uniId: string, valor: string) => void;
    lstErrores?: Record<string, string>;
}

export const CamposAdicionalesSection: React.FC<CamposAdicionalesSectionProps> = ({
    lstCampos,
    valores,
    onChange,
    lstErrores = {},
}) => {
    if (lstCampos.length === 0) return null;

    return (
        <Box>
            <SectionTitle>Información adicional</SectionTitle>
            <Stack spacing={2}>
                {lstCampos.map((campo) => {
                    const uniId = campo.uniIdSuscripcionCampoAdicional;
                    const valor = valores[uniId] ?? "";
                    const errorMsg = lstErrores[uniId];

                    if (
                        campo.tinCodSuscripcionCampoAdicionalTipo === TIPO_CAMPO_ADICIONAL.SELECTOR
                    ) {
                        const lstOpciones = campo.vchOpciones
                            ? campo.vchOpciones
                                  .split(",")
                                  .map((o) => o.trim())
                                  .filter(Boolean)
                            : [];
                        const labelId = `selector-label-${uniId}`;
                        return (
                            <FormControl
                                key={uniId}
                                fullWidth
                                required={campo.bitRequerido}
                                error={!!errorMsg}
                                sx={textFieldStyle}
                            >
                                <InputLabel id={labelId}>{campo.vchLabel}</InputLabel>
                                <Select
                                    labelId={labelId}
                                    value={valor}
                                    label={campo.vchLabel}
                                    onChange={(e) => onChange(uniId, e.target.value)}
                                >
                                    {lstOpciones.map((opcion) => (
                                        <MenuItem key={opcion} value={opcion}>
                                            {opcion}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errorMsg && <FormHelperText>{errorMsg}</FormHelperText>}
                            </FormControl>
                        );
                    }

                    if (
                        campo.tinCodSuscripcionCampoAdicionalTipo === TIPO_CAMPO_ADICIONAL.CHECKBOX
                    ) {
                        return (
                            <Box key={uniId}>
                                <FormControlLabel
                                    sx={checkboxLabelStyle}
                                    control={
                                        <Checkbox
                                            checked={valor === "true"}
                                            onChange={(e) =>
                                                onChange(uniId, e.target.checked ? "true" : "false")
                                            }
                                            sx={checkboxStyle}
                                        />
                                    }
                                    label={
                                        <Box component="span">
                                            {campo.vchLabel}
                                            {campo.bitRequerido && (
                                                <Typography
                                                    component="span"
                                                    color="error"
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    *
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />
                                {errorMsg && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ ml: 4, display: "block" }}
                                    >
                                        {errorMsg}
                                    </Typography>
                                )}
                            </Box>
                        );
                    }

                    return (
                        <TextField
                            key={uniId}
                            label={campo.vchLabel}
                            required={campo.bitRequerido}
                            value={valor}
                            onChange={(e) => onChange(uniId, e.target.value)}
                            multiline={
                                campo.tinCodSuscripcionCampoAdicionalTipo ===
                                TIPO_CAMPO_ADICIONAL.AREA_TEXTO
                            }
                            rows={
                                campo.tinCodSuscripcionCampoAdicionalTipo ===
                                TIPO_CAMPO_ADICIONAL.AREA_TEXTO
                                    ? 3
                                    : undefined
                            }
                            error={!!errorMsg}
                            helperText={errorMsg}
                            fullWidth
                            sx={textFieldStyle}
                        />
                    );
                })}
            </Stack>
        </Box>
    );
};
